































































































import {
  computed,
  defineComponent,
  PropType,
  ref,
  useRoute,
  useRouter,
  watch,
} from '@nuxtjs/composition-api';
import useResponsive from '@shared/composables/useResponsive';
import {
  lockPageScrollOnMobileOnly,
  unlockPageScrollOnMobileOnly,
} from '@shared/composables/useUiHelpers';
import useUiState from '@shared/composables/useUiState';
import { sanitizeLinkUrl } from '@shared/helpers/sanitizeLinkUrl';
import { CSSClass } from '@shared/types/vue';
import { BaseButton, BaseHeading, BaseList, BaseListItem, UCIcon } from '@shared/ui';
import clickOutside from 'vue-click-outside';

import HamburgerMenuIcon from '~/static/icons/hamburger-menu.svg?inline';

import NavigationExpandedMenu from '../NavigationExpandedMenu/NavigationExpandedMenu.vue';
import NavigationMenuItem, {
  INavigationMenuItem,
} from '../NavigationMenuItem/NavigationMenuItem.vue';

const { showAccountModal, setIsSignup } = useUiState();

export default defineComponent({
  name: 'NavigationMenu',
  directives: {
    clickOutside,
  },
  components: {
    BaseButton,
    BaseList,
    BaseListItem,
    HamburgerMenuIcon,
    NavigationExpandedMenu,
    NavigationMenuItem,
    BaseHeading,
    UCIcon,
  },
  props: {
    navigationMenuItems: {
      type: Array as PropType<INavigationMenuItem[]>,
      default: () => [],
    },
    isAuthenticated: {
      type: Boolean,
      default: false,
    },
  },
  // eslint-disable-next-line max-lines-per-function
  setup() {
    const { isMobile, isTablet } = useResponsive();
    const route = useRoute();
    const router = useRouter();

    const scrollableContent = ref<any>(null);
    const isMobileMenuOpen = ref(false);
    const activeMenuItemsIds = ref<string[]>([]);

    const getIsMenuItemActive = (menuItem: INavigationMenuItem) => {
      return activeMenuItemsIds.value?.[0] === menuItem._uid;
    };

    const getHasSubmenuItems = (menuItem: INavigationMenuItem) => {
      return !!menuItem.items && menuItem.items.length > 0;
    };

    const scrollToTop = () => {
      if (scrollableContent.value) {
        scrollableContent.value.$el.scrollTop = 0;
      }
    };

    const closeMenus = () => {
      isMobileMenuOpen.value = false;
      activeMenuItemsIds.value = [];
    };

    const openMobileMenu = (): void => {
      scrollToTop();
      isMobileMenuOpen.value = true;
    };

    const clearUpToMenuItemLevel = (level: number) => {
      activeMenuItemsIds.value = activeMenuItemsIds.value.slice(0, level);
    };

    const login = () => {
      setIsSignup(false);
      showAccountModal();
    };

    const navMyAccount = () => {
      router.push('/account');
      closeMenus();
    };

    const onOpenMenuItem = (id: string, level: number) => {
      clearUpToMenuItemLevel(level);
      activeMenuItemsIds.value = [...activeMenuItemsIds.value, id];
    };

    const onCloseMenuItem = (level: number) => {
      clearUpToMenuItemLevel(level);
    };

    const collapseCategoryLevel = () => {
      if (activeMenuItemsIds.value.length > 0) onCloseMenuItem(activeMenuItemsIds.value.length - 1);
    };

    const mobileNavigationMenuClasses = computed((): CSSClass[] => {
      const baseNavigationMenuItemsClass = 'navigation-menu__items';
      const activeNavigationMenuItemsClass = `${baseNavigationMenuItemsClass}--active`;

      return [
        baseNavigationMenuItemsClass,
        {
          [activeNavigationMenuItemsClass]: isMobileMenuOpen.value,
        },
      ];
    });

    const hasActiveMenuItems = computed((): boolean => {
      return activeMenuItemsIds.value.length > 0;
    });

    watch(
      () => route.value.path,
      () => {
        closeMenus();
      },
    );

    watch(isMobileMenuOpen, () => {
      if (isMobileMenuOpen.value) {
        lockPageScrollOnMobileOnly();
      } else {
        unlockPageScrollOnMobileOnly();
      }
    });

    return {
      isMobile,
      isTablet,
      scrollableContent,
      isMobileMenuOpen,
      activeMenuItemsIds,
      mobileNavigationMenuClasses,
      hasActiveMenuItems,
      scrollToTop,
      openMobileMenu,
      closeMenus,
      login,
      navMyAccount,
      onOpenMenuItem,
      onCloseMenuItem,
      collapseCategoryLevel,
      getIsMenuItemActive,
      getHasSubmenuItems,
      sanitizeLinkUrl,
    };
  },
});
